/**
 * Site Finder — list with photo, price, traffic, land size; filters; modal with
 * InvestmentMap (same layers as full analysis) + traffic summary + on-demand competitors.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../components/UI/Button.tsx';
import { Input } from '../components/UI/Input.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '../components/UI/Card.tsx';
import InvestmentMap from '../components/Map/InvestmentMap';
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  Fuel,
  Store,
  Filter,
  X,
  MapPinned,
  ImageOff,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8003';

/** Remove repeated comma-separated segments (bad data: "Atlanta, GA, Atlanta, Clayton, GA"). */
function dedupeAddressParts(raw) {
  if (!raw || typeof raw !== 'string') return '';
  const parts = raw
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const p of parts) {
    const key = p.toLowerCase().replace(/\s+/g, ' ');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out.join(', ');
}

function formatAddress(p) {
  if (!p?.address) return '';
  const a = p.address;
  const fromParts = [a.street, a.city, a.state, a.zip].filter(Boolean).join(', ');
  const primary = (a.fullAddress || '').trim();
  if (primary) return dedupeAddressParts(primary);
  return dedupeAddressParts(fromParts);
}

/** List headline: street address first; fallback to listing name. */
function listHeadline(p) {
  const addr = formatAddress(p);
  if (addr && String(addr).trim()) return addr.trim();
  return p?.name || 'Address not on file';
}

/** Secondary line: listing title when different from address, plus type / acres. */
function listSubline(p) {
  const addr = formatAddress(p);
  const name = p?.name || '';
  const parts = [];
  if (name && addr && name.trim() !== addr.trim()) parts.push(name);
  if (p?.property_type) parts.push(p.property_type);
  if (p?.size_acres != null) parts.push(`${Number(p.size_acres).toFixed(2)} ac`);
  return parts.join(' · ') || null;
}

/** Up to 10 nearest sensors from API (traffic enrichment). */
function NearestSensorsBlock({ sensors }) {
  const list = Array.isArray(sensors) ? sensors.slice(0, 10) : [];
  if (list.length === 0) {
    return (
      <div className="text-left w-full">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
          Nearest sensors
        </p>
        <p className="text-sm text-muted-foreground leading-relaxed">No sensor data for this listing.</p>
      </div>
    );
  }
  return (
    <div className="text-left w-full min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
        10 nearest sensors
      </p>
      <ul className="space-y-2 max-h-[min(22rem,55vh)] overflow-y-auto overscroll-contain rounded-lg border border-border bg-muted/30 p-3">
        {list.map((s, i) => {
          const mi = s.distance_miles != null ? Number(s.distance_miles) : null;
          const miLabel = mi != null && !Number.isNaN(mi) ? mi.toFixed(2) : '—';
          const aadt = s.aadt_2024 != null ? Number(s.aadt_2024) : null;
          const aadtLabel = aadt != null && !Number.isNaN(aadt) ? aadt.toLocaleString() : '—';
          const name = s.station_name || 'Unknown station';
          return (
            <li
              key={`${name}-${i}`}
              className="text-sm leading-snug border-b border-border/60 pb-2 last:border-0 last:pb-0"
            >
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-muted-foreground tabular-nums text-xs">{miLabel} mi</span>
                <span className="font-semibold tabular-nums text-primary">{aadtLabel}</span>
                <span className="text-muted-foreground text-xs">AADT</span>
              </div>
              <div className="text-foreground/90 mt-1 break-words" title={name}>
                {name}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PropertyThumb({ url }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground p-1">
        <ImageOff className="h-6 w-6 mb-0.5 opacity-60" />
        <MapPinned className="h-4 w-4 opacity-60" />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt=""
      className="w-full h-full object-cover"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

const SiteFinderPage = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);

  const [georgiaOnly, setGeorgiaOnly] = useState(true);
  /** Default on: gas-station site search is land-focused. */
  const [landOnly, setLandOnly] = useState(true);
  const [fayetteSpaldingOnly, setFayetteSpaldingOnly] = useState(false);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minTraffic, setMinTraffic] = useState('');
  const [maxTraffic, setMaxTraffic] = useState('');
  const [minAcres, setMinAcres] = useState('');
  const [maxAcres, setMaxAcres] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('traffic_desc');

  const [applied, setApplied] = useState({
    georgiaOnly: true,
    landOnly: true,
    fayetteSpaldingOnly: false,
    minPrice: '',
    maxPrice: '',
    minTraffic: '',
    maxTraffic: '',
    minAcres: '',
    maxAcres: '',
    propertyType: '',
    search: '',
    sort: 'traffic_desc',
  });

  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [traffic, setTraffic] = useState(null);
  const [competitorsPayload, setCompetitorsPayload] = useState(null);
  const [loadingTraffic, setLoadingTraffic] = useState(false);
  const [loadingComp, setLoadingComp] = useState(false);

  const subjectCoords = useMemo(() => {
    if (selected?.latitude != null && selected?.longitude != null) {
      return { lat: Number(selected.latitude), lng: Number(selected.longitude) };
    }
    return { lat: 33.749, lng: -84.388 };
  }, [selected]);

  const addressLine = useMemo(() => formatAddress(selected) || '—', [selected]);

  const mapSensors = traffic?.map_sensors || [];
  const competitorsForMap = useMemo(() => {
    const d = competitorsPayload?.competitors_data;
    if (!d) return { gas_stations: {}, convenience_stores: {} };
    return {
      gas_stations: d.gas_stations || {},
      convenience_stores: d.convenience_stores || {},
    };
  }, [competitorsPayload]);

  const alcoholZones = useMemo(() => {
    const d = competitorsPayload?.competitors_data;
    if (!d) return { churches: [], schools: [] };
    return {
      churches: d.churches || [],
      schools: d.schools || [],
    };
  }, [competitorsPayload]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        georgia_only: applied.georgiaOnly ? 'true' : 'false',
        land_only: applied.landOnly ? 'true' : 'false',
        fayette_spalding_only: applied.fayetteSpaldingOnly ? 'true' : 'false',
        sort: applied.sort || 'traffic_desc',
      });
      if (applied.minPrice) params.set('min_price', applied.minPrice);
      if (applied.maxPrice) params.set('max_price', applied.maxPrice);
      if (applied.minTraffic) params.set('min_traffic', applied.minTraffic);
      if (applied.maxTraffic) params.set('max_traffic', applied.maxTraffic);
      if (applied.minAcres) params.set('min_acres', applied.minAcres);
      if (applied.maxAcres) params.set('max_acres', applied.maxAcres);
      if (applied.propertyType.trim()) params.set('property_type', applied.propertyType.trim());
      if (applied.search.trim()) params.set('search', applied.search.trim());

      const res = await fetch(`${API_URL}/site-finder/properties?${params}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setProperties(data.properties || []);
      setTotalCount(data.pagination?.total_count ?? 0);
      setTotalPages(data.pagination?.total_pages ?? 1);
    } catch (e) {
      setListError(e.message || 'Failed to load properties');
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, applied]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setCompetitorsPayload(null);
    setTraffic(null);
    setSelected(null);
  }, []);

  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen, closeModal]);

  const applyFilters = () => {
    setApplied({
      georgiaOnly,
      landOnly,
      fayetteSpaldingOnly,
      minPrice,
      maxPrice,
      minTraffic,
      maxTraffic,
      minAcres,
      maxAcres,
      propertyType,
      search,
      sort,
    });
    setPage(1);
  };

  const openPropertyAnalysis = async (prop) => {
    setSelected(prop);
    setModalOpen(true);
    setTraffic(null);
    setCompetitorsPayload(null);
    setLoadingTraffic(true);
    try {
      const tRes = await fetch(`${API_URL}/site-finder/property/${prop.id}/traffic`);
      if (tRes.ok) setTraffic(await tRes.json());
    } catch (_) {
      /* ignore */
    } finally {
      setLoadingTraffic(false);
    }
  };

  const loadCompetitors = async () => {
    if (!selected) return;
    setLoadingComp(true);
    setCompetitorsPayload(null);
    try {
      const res = await fetch(`${API_URL}/site-finder/property/${selected.id}/competitors`, {
        method: 'POST',
      });
      if (res.ok) {
        const data = await res.json();
        setCompetitorsPayload(data);
      } else {
        setCompetitorsPayload({ error: await res.text() });
      }
    } catch (_) {
      setCompetitorsPayload({ error: 'Request failed' });
    } finally {
      setLoadingComp(false);
    }
  };

  const modalContent =
    modalOpen &&
    createPortal(
      <div
        className="fixed inset-0 z-[100] flex items-stretch justify-center p-1 sm:p-2 bg-black/60 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="site-finder-modal-title"
        onClick={(e) => {
          if (e.target === e.currentTarget) closeModal();
        }}
      >
        <div
          className="bg-background text-foreground rounded-lg sm:rounded-xl shadow-2xl border w-full max-w-[min(100vw-8px,1920px)] h-[calc(100dvh-8px)] sm:h-[calc(100dvh-16px)] flex flex-col overflow-hidden my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4 px-3 py-2 sm:px-4 sm:py-3 border-b shrink-0">
            <div className="min-w-0">
              <h2 id="site-finder-modal-title" className="text-lg font-semibold truncate pr-2">
                {selected?.name || 'Property analysis'}
              </h2>
              <p className="text-xs text-muted-foreground truncate">{addressLine}</p>
              <div className="flex flex-wrap gap-2 mt-2 text-xs">
                <span className="rounded-md bg-muted px-2 py-0.5">
                  ${(selected?.asking_price || 0).toLocaleString()}
                </span>
                {selected?.size_acres != null && (
                  <span className="rounded-md bg-muted px-2 py-0.5">
                    {Number(selected.size_acres).toFixed(2)} ac
                  </span>
                )}
                <span className="rounded-md bg-primary/15 px-2 py-0.5 font-medium">
                  Eff. AADT {(selected?.traffic_count_aadt ?? traffic?.envelope?.effective_aadt ?? 0).toLocaleString()}
                </span>
              </div>
            </div>
            <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={closeModal} aria-label="Close">
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {loadingTraffic && (
              <div className="shrink-0 flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground border-b border-border/60">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading traffic sensors for map…
              </div>
            )}

            <div className="flex-1 min-h-0 flex flex-col px-2 pt-2 pb-0 sm:px-3 sm:pt-3 overflow-hidden">
              <div className="flex-1 min-h-0 min-h-[320px] rounded-lg border bg-muted/20 overflow-hidden flex flex-col">
                <InvestmentMap
                  fillContainer
                  address={addressLine}
                  subjectCoords={subjectCoords}
                  trafficSensors={mapSensors}
                  competitors={competitorsForMap}
                  alcoholZones={alcoholZones}
                />
              </div>
            </div>

            <div className="shrink-0 max-h-[32vh] overflow-y-auto border-t border-border px-3 py-3 sm:px-4 space-y-3">
              {traffic?.envelope && (
                <div className="rounded-md bg-muted/50 p-3 space-y-1 text-sm">
                  <div className="font-medium flex items-center gap-1">
                    <Fuel className="h-4 w-4" /> Traffic envelope
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <span className="text-muted-foreground">Effective AADT</span>
                    <span className="font-semibold">
                      {traffic.envelope.effective_aadt?.toLocaleString?.() ?? '—'}
                    </span>
                    <span className="text-muted-foreground">Nearest sensor AADT</span>
                    <span>{traffic.envelope.nearest_aadt?.toLocaleString?.() ?? '—'}</span>
                    <span className="text-muted-foreground">Best within 2 mi</span>
                    <span>{traffic.envelope.best_nearby_aadt?.toLocaleString?.() ?? '—'}</span>
                    {traffic.envelope.effective_sensor && (
                      <>
                        <span className="text-muted-foreground">Driving sensor</span>
                        <span className="truncate">{traffic.envelope.effective_sensor.station_name}</span>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 items-center">
                <Button size="sm" variant="secondary" onClick={loadCompetitors} disabled={loadingComp}>
                  {loadingComp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Store className="h-4 w-4" />}
                  Load competitors (Google Places)
                </Button>
                <span className="text-xs text-muted-foreground">
                  Adds gas / c-store pins, alcohol zones, and heatmap on the map.
                </span>
              </div>

              {competitorsPayload?.error && (
                <p className="text-xs text-destructive">{String(competitorsPayload.error)}</p>
              )}

              {selected?.listing_url && (
                <a
                  href={selected.listing_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block text-xs text-primary underline"
                >
                  Open listing
                </a>
              )}
            </div>
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <div className="p-4 md:p-6 flex flex-col gap-4 min-h-0 w-full max-w-full">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Site Finder</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Filter by <strong>price</strong>, <strong>land size</strong>, and traffic thresholds. Listings show the{' '}
          <strong>10 nearest sensors</strong> (miles + AADT). Click a row for the full <strong>Investment Map</strong>.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0 min-w-0 items-stretch">
        <Card className="w-full lg:w-96 lg:min-w-[22rem] lg:max-w-[22rem] lg:shrink-0 flex flex-col overflow-hidden">
          <CardHeader className="pb-2 p-4 sm:p-6 sm:pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4 shrink-0" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 overflow-y-auto overflow-x-visible p-4 pt-0 sm:p-6 sm:pt-0">
            <label className="grid grid-cols-[auto_1fr] gap-3 items-start cursor-pointer text-sm leading-snug">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-input"
                checked={georgiaOnly}
                onChange={(e) => setGeorgiaOnly(e.target.checked)}
              />
              <span className="min-w-0">Georgia bounds only</span>
            </label>

            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 sm:p-4 space-y-3 w-full">
              <label className="grid grid-cols-[auto_1fr] gap-3 items-start cursor-pointer text-sm leading-snug">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-input"
                  checked={landOnly}
                  onChange={(e) => setLandOnly(e.target.checked)}
                />
                <span className="font-medium text-foreground min-w-0">Land listings only</span>
              </label>
              <p className="text-xs text-muted-foreground leading-relaxed">
                When on, only listings whose type or subtype contains &quot;land&quot;. Turn off to include retail,
                office, and other types.
              </p>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-3 sm:p-4 space-y-3 w-full">
              <label className="grid grid-cols-[auto_1fr] gap-3 items-start cursor-pointer text-sm leading-snug">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-input"
                  checked={fayetteSpaldingOnly}
                  onChange={(e) => setFayetteSpaldingOnly(e.target.checked)}
                />
                <span className="font-medium text-foreground min-w-0">Fayette &amp; Spalding counties only (GA)</span>
              </label>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Matches <code className="rounded bg-muted px-1 py-0.5 text-[11px]">address.county</code> when present,
                or listing coordinates inside approximate Fayette / Spalding bounding boxes when county is missing.
              </p>
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Sort by</label>
              <select
                className="mt-1 w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option value="traffic_desc">Traffic (high first)</option>
                <option value="price_desc">Price (high first)</option>
                <option value="price_asc">Price (low first)</option>
                <option value="acres_desc">Land size (large first)</option>
                <option value="acres_asc">Land size (small first)</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground">Min price</label>
                <Input type="number" placeholder="0" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Max price</label>
                <Input type="number" placeholder="no max" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground">Min eff. AADT</label>
                <Input type="number" placeholder="0" value={minTraffic} onChange={(e) => setMinTraffic(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Max eff. AADT</label>
                <Input type="number" placeholder="no max" value={maxTraffic} onChange={(e) => setMaxTraffic(e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-muted-foreground">Min acres</label>
                <Input type="number" step="0.01" placeholder="0" value={minAcres} onChange={(e) => setMinAcres(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Max acres</label>
                <Input type="number" step="0.01" placeholder="no max" value={maxAcres} onChange={(e) => setMaxAcres(e.target.value)} />
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground">Property type contains (optional)</label>
              <Input
                value={propertyType}
                onChange={(e) => setPropertyType(e.target.value)}
                placeholder="Narrows further, e.g. Commercial"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Search</label>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Address, city, listing name"
              />
            </div>
            <Button className="w-full" onClick={applyFilters}>
              Apply filters
            </Button>
            <p className="text-xs text-muted-foreground">
              Total matching: <strong>{totalCount.toLocaleString()}</strong>
            </p>
          </CardContent>
        </Card>

        <Card className="flex-1 min-w-0 flex flex-col overflow-hidden">
          <CardHeader className="py-3">
            <CardTitle className="text-base">Properties (page {page})</CardTitle>
            <p className="text-xs text-muted-foreground font-normal">
              Address, price, and the ten nearest traffic sensors. Click a row for the investment map.
            </p>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-2 sm:p-3">
            {loading && (
              <div className="flex justify-center py-8 text-muted-foreground">
                <Loader2 className="animate-spin h-6 w-6" />
              </div>
            )}
            {listError && <div className="p-4 text-destructive text-sm">{listError}</div>}
            {!loading && !listError && (
              <ul className="flex flex-col gap-2">
                {properties.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => openPropertyAnalysis(p)}
                      className={`w-full max-w-full text-left rounded-xl border bg-card shadow-sm hover:bg-accent/40 hover:border-primary/30 transition-colors flex flex-col gap-4 p-4 sm:p-5 min-w-0 ${
                        selected?.id === p.id && modalOpen
                          ? 'ring-2 ring-primary border-primary bg-primary/5'
                          : 'border-border'
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row gap-4 min-w-0">
                        <div className="w-full sm:w-32 h-40 sm:h-32 shrink-0 rounded-lg overflow-hidden bg-muted border self-center sm:self-start">
                          <PropertyThumb url={p.thumbnail_url} />
                        </div>

                        <div className="min-w-0 flex-1 flex flex-col justify-center gap-1 text-left">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Address
                          </p>
                          <p className="text-base sm:text-lg font-semibold text-foreground leading-snug">
                            {listHeadline(p)}
                          </p>
                          {listSubline(p) && (
                            <p className="text-sm text-muted-foreground mt-1">{listSubline(p)}</p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 pt-3 border-t border-border/80 min-w-0">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                            Price
                          </p>
                          <p className="text-2xl font-bold tabular-nums text-foreground">
                            ${(p.asking_price || 0).toLocaleString()}
                          </p>
                        </div>
                        <NearestSensorsBlock sensors={p.nearest_sensors} />
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
          <div className="flex items-center justify-between p-3 border-t">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((x) => Math.max(1, x - 1))}>
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <span className="text-xs text-muted-foreground">
              {page} / {totalPages || 1}
            </span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((x) => x + 1)}>
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      </div>

      {modalContent}
    </div>
  );
};

export default SiteFinderPage;
